import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const LondonVIPAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [notes, setNotes] = useState('');
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings'); // bookings, drivers, vehicles
  
  // Driver & Vehicle Management
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', phone: '', email: '', photo_url: '', license_number: '' });
  const [newVehicle, setNewVehicle] = useState({ make: '', model: '', color: '', plate: '', year: '' });

  useEffect(() => {
    // Force scroll to top immediately and after render
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    scrollToTop();
    setTimeout(scrollToTop, 0);
    requestAnimationFrame(scrollToTop);
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Check if user is admin
      const { data: adminData } = await supabase
        .from('admins')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!adminData) {
        alert('Access denied. Admin access required.');
        navigate('/');
        return;
      }

      fetchBookings();
      fetchDrivers();
      fetchVehicles();
    } catch (error) {
      console.error('Error checking auth:', error);
      navigate('/login');
    }
  };

  const fetchBookings = async () => {
    try {
      let query = supabase
        .from('vip_protect_bookings')
        .select('*')
        .order('service_date', { ascending: true })
        .order('service_time', { ascending: true });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchBookings();
    }
  }, [filter]);

  // Scroll to top when changing tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const { error } = await supabase
        .from('vip_protect_bookings')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', bookingId);

      if (error) throw error;

      fetchBookings();
      alert(`Booking ${newStatus} successfully`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating booking status');
    }
  };

  const fetchDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('is_active', true)
        .order('make');

      if (error) throw error;
      setVehicles(data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const addDriver = async () => {
    try {
      const { error } = await supabase
        .from('drivers')
        .insert([newDriver]);

      if (error) throw error;

      fetchDrivers();
      setShowAddDriverModal(false);
      setNewDriver({ name: '', phone: '', email: '', photo_url: '', license_number: '' });
      alert('Driver added successfully!');
    } catch (error) {
      console.error('Error adding driver:', error);
      alert('Error adding driver');
    }
  };

  const addVehicle = async () => {
    try {
      const { error} = await supabase
        .from('vehicles')
        .insert([newVehicle]);

      if (error) throw error;

      fetchVehicles();
      setShowAddVehicleModal(false);
      setNewVehicle({ make: '', model: '', color: '', plate: '', year: '' });
      alert('Vehicle added successfully!');
    } catch (error) {
      console.error('Error adding vehicle:', error);
      alert('Error adding vehicle');
    }
  };

  const assignDriver = async (bookingId) => {
    try {
      const driver = drivers.find(d => d.id === selectedDriverId);
      const vehicle = vehicles.find(v => v.id === selectedVehicleId);

      if (!driver || !vehicle) {
        alert('Please select both driver and vehicle');
        return;
      }

      const { error } = await supabase
        .from('vip_protect_bookings')
        .update({ 
          driver_id: driver.id,
          vehicle_id: vehicle.id,
          driver_name: driver.name,
          driver_phone: driver.phone,
          driver_photo_url: driver.photo_url,
          vehicle_make: vehicle.make,
          vehicle_model: vehicle.model,
          vehicle_color: vehicle.color,
          vehicle_plate: vehicle.plate,
          driver_assigned_at: new Date().toISOString(),
          status: 'confirmed',
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (error) throw error;

      fetchBookings();
      setShowDriverModal(false);
      setSelectedDriverId('');
      setSelectedVehicleId('');
      alert('Driver and vehicle assigned successfully!');
    } catch (error) {
      console.error('Error assigning driver:', error);
      alert('Error assigning driver');
    }
  };

  const openDriverModal = (booking) => {
    setSelectedBooking(booking);
    setSelectedDriverId(booking.driver_id || '');
    setSelectedVehicleId(booking.vehicle_id || '');
    setShowDriverModal(true);
  };

  const updateNotes = async (bookingId) => {
    try {
      const { error } = await supabase
        .from('vip_protect_bookings')
        .update({ notes: notes, updated_at: new Date().toISOString() })
        .eq('id', selectedBooking.id);

      if (error) throw error;

      fetchBookings();
      setSelectedBooking(null);
      setNotes('');
      alert('Notes updated successfully');
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Error updating notes');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#CDB688';
      case 'confirmed': return '#4CAF50';
      case 'in_progress': return '#2196F3';
      case 'completed': return '#808080';
      case 'cancelled': return '#f44336';
      default: return '#000';
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        color: 'white',
        background: getStatusColor(status),
        textTransform: 'uppercase'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    return new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingSpinner />;

  // Enhanced filtering
  const filteredBookings = bookings.filter(booking => {
    // Status filter
    const statusMatch = filter === 'all' || booking.status === filter;
    
    // Search filter (name, email, phone, addresses)
    const searchMatch = !searchTerm || 
      booking.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.user_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.pickup_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.destination_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vehicle_make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Date filter
    const dateMatch = !dateFilter || booking.service_date === dateFilter;
    
    return statusMatch && searchMatch && dateMatch;
  });

  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const inProgressCount = bookings.filter(b => b.status === 'in_progress').length;

  return (
    <div className="page-container">
      <div className="page-wrapper" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>
              London VIP Protect Admin
            </h1>
            <p style={{ color: '#666', fontSize: '0.95rem' }}>Chauffeur & Security Services Dashboard</p>
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              border: '1px solid var(--gold)',
              color: 'var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Back to Dashboard
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '2rem',
          borderBottom: '2px solid #f0f0f0',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('bookings')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'bookings' ? '3px solid var(--gold)' : '3px solid transparent',
              color: activeTab === 'bookings' ? 'var(--gold)' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('drivers')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'drivers' ? '3px solid var(--gold)' : '3px solid transparent',
              color: activeTab === 'drivers' ? 'var(--gold)' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Drivers ({drivers.length})
          </button>
          <button
            onClick={() => setActiveTab('vehicles')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'vehicles' ? '3px solid var(--gold)' : '3px solid transparent',
              color: activeTab === 'vehicles' ? 'var(--gold)' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Vehicles ({vehicles.length})
          </button>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
        <>
        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#CDB688', marginBottom: '0.5rem' }}>{pendingCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Pending Requests</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50', marginBottom: '0.5rem' }}>{confirmedCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Confirmed</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3', marginBottom: '0.5rem' }}>{inProgressCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>In Progress</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.5rem' }}>{bookings.length}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Bookings</div>
          </div>
        </div>

        {/* Filters */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          {/* Search and Date Filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                placeholder="Search by name, email, phone, address, driver, vehicle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            <div style={{ minWidth: '180px' }}>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            {(searchTerm || dateFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                }}
                style={{
                  padding: '0.75rem 1rem',
                  background: '#f44336',
                  border: 'none',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Status Filters */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {['all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                style={{
                  padding: '0.5rem 1rem',
                  background: filter === status ? 'var(--gold)' : 'transparent',
                  border: `1px solid ${filter === status ? 'var(--gold)' : '#ccc'}`,
                  color: filter === status ? '#000' : '#666',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  fontWeight: filter === status ? 'bold' : 'normal'
                }}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>

          {/* Results count */}
          <div style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
            Showing {filteredBookings.length} of {bookings.length} booking(s)
          </div>
        </div>

        {/* Bookings List */}
        <div className="card" style={{ overflow: 'hidden' }}>
          {filteredBookings.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <div>No bookings found</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(205, 182, 136, 0.1)', borderBottom: '2px solid var(--gold)' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Date & Time</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Client</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Route</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Driver & Vehicle</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <React.Fragment key={booking.id}>
                      <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <td style={{ padding: '1rem', color: '#000' }}>
                          <div style={{ fontWeight: 'bold' }}>{formatDate(booking.service_date)}</div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            {formatTime(booking.service_time)} - {formatTime(booking.end_time)}
                          </div>
                          {booking.duration_hours && (
                            <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: 'var(--gold)', fontWeight: 'bold' }}>
                              ⏱ {booking.duration_hours} hour(s)
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '1rem', color: '#000' }}>
                          <div style={{ fontWeight: 'bold' }}>{booking.user_name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>{booking.user_email}</div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>{booking.user_phone}</div>
                        </td>
                        <td style={{ padding: '1rem', color: '#000', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '0.25rem' }}>
                            <strong style={{ fontSize: '0.85rem', color: '#666' }}>From:</strong>
                            <div style={{ fontSize: '0.9rem' }}>{booking.pickup_address}</div>
                          </div>
                          <div>
                            <strong style={{ fontSize: '0.85rem', color: '#666' }}>To:</strong>
                            <div style={{ fontSize: '0.9rem' }}>{booking.destination_address}</div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: '#000' }}>
                          {booking.driver_name ? (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                {booking.driver_photo_url && (
                                  <img 
                                    src={booking.driver_photo_url} 
                                    alt={booking.driver_name}
                                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                )}
                                <div>
                                  <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{booking.driver_name}</div>
                                  <div style={{ fontSize: '0.8rem', color: '#666' }}>{booking.driver_phone}</div>
                                </div>
                              </div>
                              {booking.vehicle_make && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                  {booking.vehicle_color} {booking.vehicle_make} {booking.vehicle_model}
                                  <div style={{ fontWeight: 'bold', color: '#000' }}>{booking.vehicle_plate}</div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>Not assigned</span>
                          )}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {getStatusBadge(booking.status)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {booking.status === 'pending' && (
                              <button
                                onClick={() => openDriverModal(booking)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'var(--gold)',
                                  border: '2px solid var(--gold)',
                                  color: 'black',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                Assign Driver
                              </button>
                            )}
                            {(booking.status === 'confirmed' || booking.status === 'in_progress' || booking.status === 'completed') && booking.driver_name && (
                              <button
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowDetailsModal(true);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#2196F3',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                View Details
                              </button>
                            )}
                            {booking.status === 'confirmed' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'in_progress')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#4CAF50',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Start Service
                              </button>
                            )}
                            {booking.status === 'in_progress' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'completed')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#808080',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Complete
                              </button>
                            )}
                            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#f44336',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Cancel
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setNotes(booking.notes || '');
                              }}
                              style={{
                                padding: '0.4rem 0.8rem',
                                background: 'var(--gold)',
                                border: 'none',
                                color: 'black',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 'bold'
                              }}
                            >
                              Notes
                            </button>
                          </div>
                        </td>
                      </tr>
                      {selectedBooking?.id === booking.id && (
                        <tr style={{ background: 'rgba(205, 182, 136, 0.05)' }}>
                          <td colSpan="6" style={{ padding: '1rem' }}>
                            <div style={{ background: 'rgba(0, 0, 0, 0.02)', padding: '1rem', borderRadius: '4px', border: '1px solid rgba(205, 182, 136, 0.3)' }}>
                              <h4 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Notes & Details</h4>
                              <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add notes about this booking..."
                                style={{
                                  width: '100%',
                                  minHeight: '100px',
                                  padding: '0.75rem',
                                  background: 'white',
                                  border: '1px solid rgba(205, 182, 136, 0.3)',
                                  borderRadius: '4px',
                                  color: '#000',
                                  resize: 'vertical',
                                  marginBottom: '1rem'
                                }}
                              />
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => updateNotes(booking.id)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'var(--gold)',
                                    border: 'none',
                                    color: 'black',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  Save Notes
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedBooking(null);
                                    setNotes('');
                                  }}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'transparent',
                                    border: '1px solid #ccc',
                                    color: '#666',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Manage Drivers</h3>
              <button
                onClick={() => setShowAddDriverModal(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--gold)',
                  color: 'black',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                + Add Driver
              </button>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Name</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Phone</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Email</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>License</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((driver) => (
                    <tr key={driver.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {driver.photo_url && (
                            <img src={driver.photo_url} alt={driver.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                          )}
                          <span style={{ fontWeight: 'bold' }}>{driver.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>{driver.phone}</td>
                      <td style={{ padding: '1rem' }}>{driver.email || '-'}</td>
                      <td style={{ padding: '1rem' }}>{driver.license_number || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Manage Vehicles</h3>
              <button
                onClick={() => setShowAddVehicleModal(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--gold)',
                  color: 'black',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                + Add Vehicle
              </button>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Make & Model</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Color</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>License Plate</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Year</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '1rem', fontWeight: 'bold' }}>{vehicle.make} {vehicle.model}</td>
                      <td style={{ padding: '1rem' }}>{vehicle.color}</td>
                      <td style={{ padding: '1rem' }}><span style={{ padding: '0.25rem 0.5rem', background: '#f0f0f0', borderRadius: '4px', fontFamily: 'monospace' }}>{vehicle.plate}</span></td>
                      <td style={{ padding: '1rem' }}>{vehicle.year || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Driver Modal */}
      {showAddDriverModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>Add New Driver</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Name *"
                value={newDriver.name}
                onChange={(e) => setNewDriver({...newDriver, name: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="tel"
                placeholder="Phone *"
                value={newDriver.phone}
                onChange={(e) => setNewDriver({...newDriver, phone: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="email"
                placeholder="Email"
                value={newDriver.email}
                onChange={(e) => setNewDriver({...newDriver, email: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="License Number"
                value={newDriver.license_number}
                onChange={(e) => setNewDriver({...newDriver, license_number: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="url"
                placeholder="Photo URL"
                value={newDriver.photo_url}
                onChange={(e) => setNewDriver({...newDriver, photo_url: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={addDriver}
                  disabled={!newDriver.name || !newDriver.phone}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    opacity: (!newDriver.name || !newDriver.phone) ? 0.5 : 1
                  }}
                >
                  Add Driver
                </button>
                <button
                  onClick={() => setShowAddDriverModal(false)}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddVehicleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>Add New Vehicle</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Make (e.g., BMW) *"
                value={newVehicle.make}
                onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Model (e.g., i7) *"
                value={newVehicle.model}
                onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Color *"
                value={newVehicle.color}
                onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="License Plate *"
                value={newVehicle.plate}
                onChange={(e) => setNewVehicle({...newVehicle, plate: e.target.value.toUpperCase()})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', textTransform: 'uppercase' }}
              />
              <input
                type="number"
                placeholder="Year"
                value={newVehicle.year}
                onChange={(e) => setNewVehicle({...newVehicle, year: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={addVehicle}
                  disabled={!newVehicle.make || !newVehicle.model || !newVehicle.color || !newVehicle.plate}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    opacity: (!newVehicle.make || !newVehicle.model || !newVehicle.color || !newVehicle.plate) ? 0.5 : 1
                  }}
                >
                  Add Vehicle
                </button>
                <button
                  onClick={() => setShowAddVehicleModal(false)}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver Assignment Modal */}
      {showDriverModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Assign Driver & Vehicle</h3>
              <button
                onClick={() => setShowDriverModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}><strong>Booking for:</strong> {selectedBooking.user_name}</p>
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}><strong>Date:</strong> {new Date(selectedBooking.service_date).toLocaleDateString()}</p>
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}><strong>Time:</strong> {selectedBooking.service_time} - {selectedBooking.end_time}</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#000' }}>Select Driver *</label>
                <select
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  required
                >
                  <option value="">-- Choose a driver --</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name} - {driver.phone}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#000' }}>Select Vehicle *</label>
                <select
                  value={selectedVehicleId}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  required
                >
                  <option value="">-- Choose a vehicle --</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.color} {vehicle.make} {vehicle.model} ({vehicle.plate})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => assignDriver(selectedBooking.id)}
                  disabled={!selectedDriverId || !selectedVehicleId}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    opacity: (!selectedDriverId || !selectedVehicleId) ? 0.5 : 1
                  }}
                >
                  Assign & Confirm Booking
                </button>
                <button
                  onClick={() => setShowDriverModal(false)}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Booking Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {/* Booking Info */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Service Information</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ color: '#666' }}>Date:</strong>{' '}
                    <span>{new Date(selectedBooking.service_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Time:</strong>{' '}
                    <span>{selectedBooking.service_time} - {selectedBooking.end_time}</span>
                  </div>
                  {selectedBooking.duration_hours && (
                    <div>
                      <strong style={{ color: '#666' }}>Duration:</strong>{' '}
                      <span>{selectedBooking.duration_hours} hour(s)</span>
                    </div>
                  )}
                  <div>
                    <strong style={{ color: '#666' }}>Status:</strong>{' '}
                    {getStatusBadge(selectedBooking.status)}
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Client Details</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ color: '#666' }}>Name:</strong>{' '}
                    <span>{selectedBooking.user_name}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Email:</strong>{' '}
                    <span>{selectedBooking.user_email}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Phone:</strong>{' '}
                    <span>{selectedBooking.user_phone}</span>
                  </div>
                </div>
              </div>

              {/* Route Info */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Route</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ color: '#666' }}>Pickup:</strong>{' '}
                    <div>{selectedBooking.pickup_address}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Destination:</strong>{' '}
                    <div>{selectedBooking.destination_address}</div>
                  </div>
                </div>
              </div>

              {/* Driver & Vehicle Info */}
              {selectedBooking.driver_name && (
                <div style={{ padding: '1rem', background: 'rgba(205, 182, 136, 0.1)', border: '2px solid var(--gold)', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: 'var(--gold)' }}>Assigned Driver & Vehicle</h4>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                        {selectedBooking.driver_photo_url && (
                          <img 
                            src={selectedBooking.driver_photo_url} 
                            alt={selectedBooking.driver_name}
                            style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--gold)' }}
                          />
                        )}
                        <div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{selectedBooking.driver_name}</div>
                          <div style={{ color: '#666' }}>{selectedBooking.driver_phone}</div>
                        </div>
                      </div>
                    </div>
                    {selectedBooking.vehicle_make && (
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid rgba(205, 182, 136, 0.3)' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                          {selectedBooking.vehicle_color} {selectedBooking.vehicle_make} {selectedBooking.vehicle_model}
                        </div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--gold)' }}>
                          {selectedBooking.vehicle_plate}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--gold)',
                  border: 'none',
                  color: 'black',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LondonVIPAdminDashboard;
